import { useCallback } from "react";

import { useSelector } from "react-redux";

import { selectWorkerUri } from "Services/state/agent/AgentInformationSlice";

import { CallParticipant } from "../../../CallPanel/TaskInfo/CallParticipant/CallParticipant";
import { PendingParticipant } from "../../../CallPanel/TaskInfo/PendingParticipant/PendingParticipant";
import { TransfersPanel, type TransferPanelHeaderRenderer } from "../../../CallPanel/TransfersPanel/TransfersPanel";
import { useConferenceParticipants } from "../../hooks/useConferenceParticipants/useConferenceParticipants";
import { TransfersHeader } from "./TransfersHeader";

import type { ConferenceParticipant, RVTask } from "@regal-voice/shared-types";

import styles from "./TransfersWithParticipants.module.scss";

export type TransfersWithParticipantsProps = {
    activeConferenceTask?: RVTask;
    onCloseRequest: () => void;
};

export function TransfersWithParticipants({
    activeConferenceTask,
    onCloseRequest,
}: TransfersWithParticipantsProps): JSX.Element {
    const workerUri = useSelector(selectWorkerUri);
    const { activeParticipants } = useConferenceParticipants({
        task: activeConferenceTask,
    });

    const renderPanelHeader = useCallback<TransferPanelHeaderRenderer>(
        (headerProps) => <TransfersHeader {...headerProps} />,
        []
    );

    if (!workerUri || !activeConferenceTask) {
        return <></>;
    }

    return (
        <div className={styles.container}>
            <TransfersPanel
                currentWorkerUri={workerUri}
                conferenceFriendlyId={activeConferenceTask?.attributes.conferenceFriendlyId}
                regalVoicePhone={activeConferenceTask?.attributes.regalVoicePhone}
                onCloseRequest={onCloseRequest}
                taskSid={activeConferenceTask?.taskSid}
                closeOnTransfer={false}
                asPopover
                renderHeader={renderPanelHeader}
            />

            <ParticipantTransfer participants={activeParticipants} activeConferenceTask={activeConferenceTask} />
        </div>
    );
}

const checkIsCallPending = (participant: ConferenceParticipant): boolean => {
    return participant.type == "queue" || (participant.status === "ringing" && participant.type === "internal");
};

type NonQueueParticipant = Exclude<ConferenceParticipant, { type: "queue" }>;

export const ParticipantTransfer = ({
    participants,
    activeConferenceTask,
}: {
    participants: Array<ConferenceParticipant>;
    activeConferenceTask: RVTask;
}) => {
    const hasParticipants = participants.length > 1;
    return (
        (hasParticipants && (
            <div className={styles.participants}>
                {participants.map((participant) => {
                    const isCallPending = checkIsCallPending(participant);
                    if (isCallPending) {
                        return (
                            <PendingParticipant
                                key={participant.label}
                                taskSid={participant.taskSid}
                                participantName={participant.label}
                                isQueue={participant.type === "queue"}
                            />
                        );
                    }
                    return (
                        <CallParticipant
                            key={participant.label}
                            participant={participant as NonQueueParticipant}
                            participants={participants}
                            task={activeConferenceTask}
                        />
                    );
                })}
            </div>
        )) || <></>
    );
};
